import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

function AddressModal({ 
  open, 
  onClose, 
  onSave, 
  initialAddress = null,
  title = "Address",
  addressType = "shipping", // "shipping", "postal", "billing"
  saveToProfile = true
}) {
  const [address, setAddress] = useState({
    name: '',
    email: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'AU'
  });
  
  const [loading, setLoading] = useState(false);

  // Load initial address when modal opens
  useEffect(() => {
    if (open) {
      if (initialAddress) {
        setAddress(initialAddress);
      } else {
        // Try to load from user profile
        loadUserAddress();
      }
    }
  }, [open, initialAddress]);

  const loadUserAddress = async () => {
    try {
      const response = await axios.get('/user/profile');
      const profile = response.data;
      
      if (profile) {
        // Determine which address to load based on addressType
        let savedAddress = null;
        
        if (addressType === 'postal' && profile.postal_address) {
          savedAddress = profile.postal_address;
        } else if (addressType === 'billing' && profile.billing_address) {
          savedAddress = profile.billing_address;
        } else {
          // Default: try to populate from any available data
          savedAddress = {
            name: profile.name || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address_line_1: profile.address_line_1 || '',
            address_line_2: profile.address_line_2 || '',
            city: profile.city || '',
            state: profile.state || '',
            postal_code: profile.postal_code || '',
            country: profile.country || 'AU'
          };
        }
        
        if (savedAddress && savedAddress.name) {
          setAddress(savedAddress);
        }
      }
    } catch (error) {
      console.error('Failed to load user address:', error);
    }
  };

  const validateAddress = () => {
    const required = ['name', 'email', 'address_line_1', 'city', 'state', 'postal_code'];
    for (const field of required) {
      if (!address[field] || address[field].trim() === '') {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    
    // Basic email validation
    if (!address.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateAddress()) {
      return;
    }

    setLoading(true);
    try {
      // Save to user profile if enabled
      if (saveToProfile) {
        // Get current user to check if email changed
        let currentUser = null;
        try {
          const userResponse = await axios.get('/user/profile');
          currentUser = userResponse.data;
        } catch (err) {
          console.error('Failed to get current user:', err);
        }
        
        const profileUpdate = { ...address };
        
        // Don't send email if it hasn't changed (prevents auth issues)
        if (currentUser && profileUpdate.email === currentUser.email) {
          delete profileUpdate.email;
        }
        
        // Add address type specific fields
        if (addressType === 'postal') {
          profileUpdate.postal_address = address;
        } else if (addressType === 'billing') {
          profileUpdate.billing_address = address;
        }
        
        await axios.put('/user/profile', profileUpdate);
        toast.success('Address saved to profile');
      }
      
      // Call the parent's onSave callback
      if (onSave) {
        onSave(address);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save address:', error);
      toast.error(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {addressType === 'billing' && 'Enter your billing address details'}
            {addressType === 'postal' && 'Enter your postal/mailing address details'}
            {addressType === 'shipping' && 'Enter your delivery address details'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="John Smith"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={address.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="john@example.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={address.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="+61 400 000 000"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address.address_line_1}
                onChange={(e) => handleChange('address_line_1', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123 Main Street"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apartment, suite, etc.
              </label>
              <input
                type="text"
                value={address.address_line_2}
                onChange={(e) => handleChange('address_line_2', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Unit 5"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sydney"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State/Province <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address.state}
                onChange={(e) => handleChange('state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="NSW"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2000"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                value={address.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="AU">Australia</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="NZ">New Zealand</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Saving...' : 'Save Address'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddressModal;
